import _ from 'lodash';
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {inIdleOrHiddenCallbackAsync} from '../../helpers/async/inIdleCallback';
import {buildCancelTokenSource, CancelError, CancelTokenSource} from '../../helpers/asyncCancelHelpers';
import {isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {getTagChildrenIds} from '../../helpers/models/tagHelpers';
import {ResolveDispatchProps} from '../../helpers/reduxHelpers';
import {makeMapDispatchToProps, makeMapStateToProps, ResolveStateProps} from '../../helpers/reselectHelpers';
import {ApiSearchTags, SearchTagsModel} from '../../models/api/searchTagModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {
  SettingsResourcePagesEnum,
  SettingsRootPagesEnum,
  SettingsTabsPagesEnum,
  SettingsTagsUrl,
} from '../../models/url/settingsUrlModel';
import {fetchApiAsync} from '../../services/http/http';
import {
  InterfaceEventsEnum,
  SettingsTrackingTypesEnum,
  SingleTagCreationEntryPointsEnum,
  trackInterfaceEvent,
} from '../../services/tracking';
import {AppState} from '../../store/appState';
import {getCompany} from '../../store/data/company/companySelectors';
import {updateDataAction} from '../../store/data/dataGroupsActions';
import {getTags} from '../../store/data/tags/tagsSelectors';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {addToSettingsTagsList} from '../../store/panels/settings/settingsPanelActions';
import {navigateToLinkAction} from '../../store/stateless/navigationActions';
import {SettingsTagsContext, SettingsTagsContextProps} from './settingsTagsContext';
import {SettingsTagsListContainer} from './settingsTagsListContainer';
import {SettingsSingleTagContainer} from './singleTag/settingsSingleTagContainer';

/*
 * Props.
 */

interface SettingsTagsContainerProps {
  panelId: SettingsPanelId;
  url: SettingsTagsUrl;
}

interface SettingsTagsContainerComponentProps
  extends SettingsTagsContainerProps,
    ResolveStateProps<typeof mapStateToProps>,
    ResolveDispatchProps<typeof mapDispatchToProps> {}

/*
 * Selectors.
 */

function getTagsUrl(_state: AppState, props: SettingsTagsContainerProps) {
  return props.url;
}

function makeGetCurrentTagHasChildren() {
  return createSelector([getTagsUrl, getTags], (tagsUrl, tags): boolean => {
    const {tagId} = tagsUrl;

    if (!tagId) {
      return false;
    }

    const tag = tags[tagId];
    if (!tag) {
      return false;
    }

    const isInArchivedView = tagsUrl.tabPage === SettingsTabsPagesEnum.ARCHIVED;

    return getTagChildrenIds(tag, isInArchivedView).length > 0;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SettingsTagsContainerProps>()(() => ({
  company: getCompany,
  currentTagHasChildren: makeGetCurrentTagHasChildren(),
}));

const mapDispatchToProps = makeMapDispatchToProps<SettingsTagsContainerProps>()(() => ({
  navigateToLinkAction,
  updateDataAction,
  addToSettingsTagsList,
}));

const SettingsTagsContainerComponent: FC<SettingsTagsContainerComponentProps> = (props) => {
  const {navigateToLinkAction: navigateToLink, url: settingsTagsUrl} = props;
  const {rootPage, resourcePage} = settingsTagsUrl;
  const [tagsCache, setTagsCache] = useState<ReadonlyMap<string, SearchTagsModel>>(new Map());
  const [singleTagEntryPoint, setSingleTagEntryPoint] = useState<SingleTagCreationEntryPointsEnum>();
  // Request cancellation.
  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  function maybeCancelRequest() {
    const currentCancelTokenSource = cancelTokenSourceRef.current;
    return currentCancelTokenSource && currentCancelTokenSource.cancel();
  }

  const saveSearchTagsInState = (tagSearch: SearchTagsModel) => {
    const flatTags = _(tagSearch.tags).flattenDeep().value();

    props.updateDataAction(
      {
        tags: flatTags,
      },
      {
        isComingFromPusher: false,
        shouldNotCascade: true,
      },
    );
  };

  useEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_PAGE_VIEW, {
      settings_page: 'tags',
      settings_type: isTeamNamespace(settingsTagsUrl.namespace)
        ? SettingsTrackingTypesEnum.TEAM
        : SettingsTrackingTypesEnum.PERSONAL,
      namespace: settingsTagsUrl.namespace,
      settings_subpage: settingsTagsUrl.tabPage,
    });
  }, [settingsTagsUrl.namespace, settingsTagsUrl.tabPage]);

  const requestTagsBase = async (url: string, canBeCancelled: boolean, forceRefresh?: boolean) => {
    const resultFromCache = tagsCache.get(url);
    if (resultFromCache && !forceRefresh) {
      return resultFromCache;
    }

    if (!resultFromCache && canBeCancelled) {
      maybeCancelRequest();
    }

    const cancelTokenSource = buildCancelTokenSource();
    cancelTokenSourceRef.current = cancelTokenSource;

    try {
      const tagSearch = await fetchApiAsync(ApiSearchTags, url, {
        cancelToken: cancelTokenSource.token,
      });

      setTagsCache((prev) => new Map([...prev, [url, tagSearch]]));
      inIdleOrHiddenCallbackAsync(() => saveSearchTagsInState(tagSearch), {});

      return tagSearch;
    } catch (e) {
      if (!(e instanceof CancelError)) {
        throw e;
      }

      return undefined;
    }
  };

  const requestTags = async (url: string, canBeCancelled: boolean, forceRefresh?: boolean) => {
    const tagSearch = await requestTagsBase(url, canBeCancelled, forceRefresh);

    if (tagSearch) {
      props.addToSettingsTagsList(props.panelId, tagSearch);
    }

    return tagSearch;
  };

  const openParentTag = useCallback(
    (tagId?: number) => {
      const newUrl = buildSettingsPath({
        ...settingsTagsUrl,
        namespace: settingsTagsUrl.namespace,
        tagId,
        resourcePage: SettingsResourcePagesEnum.OVERVIEW,
      });

      navigateToLink(newUrl);
    },
    [navigateToLink, settingsTagsUrl],
  );

  const openSingleTag = useCallback(
    (tagId: number) => {
      const newUrl = buildSettingsPath({
        ...settingsTagsUrl,
        tagId,
        resourcePage: SettingsResourcePagesEnum.EDIT,
      });

      navigateToLink(newUrl);
    },
    [navigateToLink, settingsTagsUrl],
  );

  const openNewTag = useCallback(
    (parentTagId: number | undefined, entryPoint: SingleTagCreationEntryPointsEnum) => {
      const newUrl = buildSettingsPath({
        ...settingsTagsUrl,
        resourcePage: SettingsResourcePagesEnum.NEW,
        tagId: parentTagId,
      });

      setSingleTagEntryPoint(entryPoint);

      navigateToLink(newUrl);
    },
    [navigateToLink, settingsTagsUrl],
  );

  const onSingleTagCancelClick = useCallback(() => {
    const newUrl = buildSettingsPath({
      ...settingsTagsUrl,
      tagId: props.currentTagHasChildren ? settingsTagsUrl.tagId : undefined,
      resourcePage: SettingsResourcePagesEnum.OVERVIEW,
    });

    navigateToLink(newUrl);
  }, [navigateToLink, props.currentTagHasChildren, settingsTagsUrl]);

  const clearTagsCache = () => {
    setTagsCache(new Map());
  };

  const settingsTagsContextValue: SettingsTagsContextProps = {
    clearTagsCache,
    requestTags,
    singleTagCreationEntryPoint: singleTagEntryPoint,
  };

  if (rootPage !== SettingsRootPagesEnum.TAGS) {
    return null;
  }

  if (resourcePage === SettingsResourcePagesEnum.EDIT) {
    return (
      <SettingsTagsContext.Provider value={settingsTagsContextValue}>
        <SettingsSingleTagContainer
          onCancelClick={onSingleTagCancelClick}
          tagsUrl={props.url}
          openParentTag={openParentTag}
          isEditing
        />
      </SettingsTagsContext.Provider>
    );
  }

  if (resourcePage === SettingsResourcePagesEnum.NEW) {
    return (
      <SettingsTagsContext.Provider value={settingsTagsContextValue}>
        <SettingsSingleTagContainer
          onCancelClick={onSingleTagCancelClick}
          tagsUrl={props.url}
          openParentTag={openParentTag}
        />
      </SettingsTagsContext.Provider>
    );
  }

  if (resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return (
      <SettingsTagsContext.Provider value={settingsTagsContextValue}>
        <SettingsTagsListContainer
          key={`tag-${props.url.namespace}-${props.url.tagId}-${props.url.tabPage}`}
          panelId={props.panelId}
          tagsUrl={props.url}
          company={props.company}
          openParentTag={openParentTag}
          openSingleTag={openSingleTag}
          openNewTag={openNewTag}
        />
      </SettingsTagsContext.Provider>
    );
  }

  return null;
};

export const SettingsTagsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SettingsTagsContainerComponent);
